import React from "react";
import { Link } from "react-router-dom";
import { API } from "../lib/api";
import { MapPin, Zap, ImageOff } from "lucide-react";
import { useI18n } from "../lib/i18n";
import FavoriteButton from "./FavoriteButton";

function isBoosted(l) {
  if (!l?.boosted_until) return false;
  try { return new Date(l.boosted_until) > new Date(); } catch { return false; }
}

export default function ListingCard({ listing, index = 0 }) {
  const { t } = useI18n();
  const firstImage = listing.image_ids?.[0];
  const uploadedUrl = firstImage ? `${API}/files/${firstImage}` : null;
  // Only seeded example listings fall back to a curated topic-matching photo.
  // Real user listings NEVER auto-show a stock image — we show a clean placeholder instead.
  const exampleUrl = listing.is_example ? listing.external_image_url : null;
  const imgUrl = uploadedUrl || exampleUrl;
  const boosted = isBoosted(listing);

  return (
    <Link
      to={`/listings/${listing.id}`}
      data-testid={`listing-card-${listing.id}`}
      className="kav-card overflow-hidden group block relative"
      style={{ animationDelay: `${index * 40}ms` }}
    >
      <div className="absolute top-3 right-3 z-10">
        <FavoriteButton listingId={listing.id} size={16} />
      </div>
      {listing.is_example && (
        <div className="absolute top-3 left-3 z-10 kav-chip !bg-[#143122] !text-white !border-[#143122]">
          Example
        </div>
      )}
      {boosted && (
        <div
          data-testid={`boost-badge-${listing.id}`}
          className={`absolute z-10 inline-flex items-center gap-1 kav-chip !bg-[#B58A2F] !text-white !border-[#B58A2F] ${listing.is_example ? "top-11 left-3" : "top-3 left-3"}`}
        >
          <Zap size={12} strokeWidth={1.75} fill="#FAFAFA" /> Boosted
        </div>
      )}
      <div className="aspect-[4/5] overflow-hidden bg-[#F4F1E9]">
        {imgUrl ? (
          <img
            src={imgUrl}
            alt={listing.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div
            data-testid={`listing-no-image-${listing.id}`}
            className="w-full h-full flex flex-col items-center justify-center text-[#1A1F1C]/40 gap-2"
          >
            <ImageOff size={28} strokeWidth={1.25} />
            <span className="text-xs uppercase tracking-wider">{t("no_photo")}</span>
          </div>
        )}
      </div>
      <div className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          <span className="overline text-[#143122]">{listing.type}</span>
          {listing.price != null && (
            <span className="text-sm font-medium text-[#143122]">
              {listing.price.toLocaleString()} {listing.currency || "EUR"}
            </span>
          )}
        </div>
        <div className="font-serif-display text-xl leading-tight text-[#1A1F1C] line-clamp-2">
          {listing.title}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-[#1A1F1C]/60">
          <MapPin size={12} strokeWidth={1.5} />
          <span>{listing.location}</span>
        </div>
      </div>
    </Link>
  );
}
